import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { memo, MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as amplitude from '@amplitude/analytics-browser';

import {
  Breadcrumbs,
  SkeletonLoader,
  Toggle,
} from '@unified-checkout/design-system';

import {
  REACH_PAGE_EVENTS,
  REACH_PAGE_EVENTS_PROPERTIES,
} from '@app/dashboard/constants/tracking_events/reach';
import {
  SYSTEM_EVENTS_GROUPING_CODES,
  SYSTEM_EVENTS_SRC_FEATURES,
} from '@app/dashboard/constants/tracking_events/system';
import { INNER_ROUTES } from '@app/dashboard/constants/inner-routes';
import { notify, useNotificationStore } from '@app/dashboard/utils/notifier';
import { useApplyThenNavigate } from '@app/dashboard/hooks/useApplyThenNavigate';
import { terminateCampaign } from '@app/dashboard/services/inner_layout/reach/campaigns/singleCampaign';
import { onToasterShowHandler } from '@app/dashboard/utils/tracking/onToasterShowHandler';

import { ConfirmationModal } from '../../../../components/ConfirmationModal';

import styles from './header.module.scss';
import { NOTIFICATION_STATUS } from '@app/dashboard/constants/notifier';

const Header = ({
  id,
  campaignDetails,
}: {
  id: string | undefined;
  campaignDetails: {
    campaignDetails: any;
    isCampaignDetailsLoading: boolean;
    isCampaignDetailsError: boolean;
  };
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const applyThenNavigate = useApplyThenNavigate();
  const { show: isNotifyShown, state: notifyState } = useNotificationStore();

  const { campaignDetails: campaignSource, isCampaignDetailsLoading } =
    campaignDetails;

  const isCampaignActive =
    typeof campaignSource?.is_terminated === 'boolean'
      ? campaignSource?.is_terminated
      : false;

  const {
    mutate: terminateCampaignMutate,
    isPending: isTerminateCampaignLoading,
  } = useMutation({
    mutationFn: () => terminateCampaign(id as string),
    onSuccess: () => {
      amplitude.track(
        REACH_PAGE_EVENTS.CAMPAIGNS_PAGE.CAMPAIGN_TERMINATE_SUCCEEDED
      );
      const successMessage = t(`${localizeKey}.successMessage`, { id });

      notify.success(successMessage, {
        onShowHandler: () =>
          onToasterShowHandler({
            type: 'success',
            trackingCode:
              SYSTEM_EVENTS_GROUPING_CODES.REACH.CAMPAIGN_DETAILS
                .CAMPAIGN_TERMINATION,
            msg: successMessage,
            srcFeature: SYSTEM_EVENTS_SRC_FEATURES.REACH.CAMPAIGN_DETAILS,
          }),
      });

      setTimeout(() => {
        applyThenNavigate(()=> setIsModalOpen(false), INNER_ROUTES.CAMPAIGNS);
      }, 2500);
    },
    onError: (axiosErr: AxiosError<{ message?: string; data?: any }>) => {
      let message = t('errors.somethingWentWrong');

      if (axiosErr.response?.data?.message) {
        message = axiosErr.response?.data?.message;
      } else if (axiosErr.message) {
        message = axiosErr.message;
      }

      amplitude.track(
        REACH_PAGE_EVENTS.CAMPAIGNS_PAGE.CAMPAIGN_TERMINATE_FAILED,
        {
          [REACH_PAGE_EVENTS_PROPERTIES.SHARED.ERROR_MESSAGE]: message,
          ...axiosErr.response?.data?.data,
        }
      );
      notify.error(message, {
        onShowHandler: () =>
          onToasterShowHandler({
            type: 'error',
            trackingCode:
              SYSTEM_EVENTS_GROUPING_CODES.REACH.CAMPAIGN_DETAILS
                .CAMPAIGN_TERMINATION,
            msg: message,
            srcFeature: SYSTEM_EVENTS_SRC_FEATURES.REACH.CAMPAIGN_DETAILS,
          }),
      });
    },
  });

  const localizeKey = `innerLayout.reach.campaigns.singleCampaignPage.modals.terminateCampaignModal.${
    isCampaignActive ? 'enabled' : 'disabled'
  }`;

  const handleToggleChange = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setIsModalOpen((prev) => !prev);
    amplitude.track(
      REACH_PAGE_EVENTS.CAMPAIGNS_PAGE.CAMPAIGN_TERMINATE_SELECTED
    );
  };

  const shouldDisableActions = isTerminateCampaignLoading || (isNotifyShown && notifyState === NOTIFICATION_STATUS.SUCCESS);

  return (
    <header className={styles['single-campaign__header']}>
      <Breadcrumbs
        linksProps={[
          {
            text: t('innerLayout.reach.campaigns.header.title'),
            onClickHandler: () => navigate(INNER_ROUTES.CAMPAIGNS),
            active: true,
          },
          {
            text: t('innerLayout.reach.campaigns.singleCampaignPage.id', {
              id,
            }),
          },
        ]}
      /> 
      <ConfirmationModal
        open={isModalOpen}
        modalContent={{
          title: t(`${localizeKey}.title`, { id }),
          supportingText: t(`${localizeKey}.supportingText`),
        }}
        primaryBtn={{
          text: t(`${localizeKey}.actions.confirm`),
          color: 'primary',
          onClick: terminateCampaignMutate,
          loading: isTerminateCampaignLoading,
          disabled: shouldDisableActions
        }}
        secondaryBtn={{
          text: t(`${localizeKey}.actions.cancel`),
          onClick: () => {
            setIsModalOpen(false);
          },
          disabled: shouldDisableActions,
        }}
      />

      <section className={styles['single-campaign__header-manage']}>
        <div className={styles['single-campaign__header-manage-content']}>
          <h1
            className={styles['single-campaign__header-manage-content-title']}
          >
            {t(
              'innerLayout.reach.campaigns.singleCampaignPage.manage.manageCampaign'
            )}
          </h1>
          <p
            className={
              styles['single-campaign__header-manage-content-subtitle']
            }
          >
            {t(
              'innerLayout.reach.campaigns.singleCampaignPage.manage.manageCampaignSubTitle'
            )}
          </p>
        </div>

        {isCampaignDetailsLoading ? (
          <SkeletonLoader height="58px" width="228px" noOfRows={1} />
        ) : (
          <div
            onClick={handleToggleChange}
            className={styles['single-campaign__header-manage-terminate']}
          >
            <span
              className={
                styles['single-campaign__header-manage-terminate-text']
              }
            >
              {t(
                'innerLayout.reach.campaigns.singleCampaignPage.manage.terminateCampaign'
              )}
            </span>
            <Toggle
              checked={isCampaignActive}
              key="terminate-campaign-toggle"
              disabled
            />
          </div>
        )}
      </section>
    </header>
  );
};

export default memo(Header);
